'use client'

import { useState } from 'react'
import { useAsync } from 'react-async-hook'

import isEmpty from 'lodash/fp/isEmpty'
import sortBy from 'lodash/fp/sortBy'

import type {
  ChildrenProps,
  CoreUser,
  Newsletters,
  NewslettersContext as NewslettersContextValue,
} from '../types'

import { toast } from '../components/Toaster'
import { NewslettersContext } from '../contexts/NewslettersContext'
import useApi from '../hooks/useApi'
import useLogger from '../hooks/useLogger'
import useTranslations from '../hooks/useTranslations'
import useUser from '../hooks/useUser'

type Props = {
  includeDisabled?: boolean
} & ChildrenProps
export default function NewslettersProvider({
  children,
  includeDisabled = false,
}: Props) {
  const $t = useTranslations()

  const endpoints = useApi()

  const logger = useLogger()

  const { isLoading: isUserLoading, user } = useUser<CoreUser>()

  let asyncNewsletters: ReturnType<
    typeof useAsync<Newsletters | null, Array<CoreUser | null>>
  >

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - I don't understand why we have a ts(7022) error here... seems
  // like a bug in TypeScript.
  // See https://github.com/microsoft/TypeScript/issues/51434
  const [newslettersContext, setNewslettersContext] =
    useState<NewslettersContextValue>({
      error: null,
      isLoading: true,
      newsletters: null,
      reload: () => {
        setNewslettersContext(
          (previousNewslettersContext: NewslettersContextValue) => ({
            ...previousNewslettersContext,
            isLoading: true,
          }),
        )

        asyncNewsletters.execute()
      },
      setError: (error: Error | null) =>
        setNewslettersContext(
          (previousNewslettersContext: NewslettersContextValue) => ({
            ...previousNewslettersContext,
            error,
            isLoading: false,
            newsletters: null,
          }),
        ),
      setNewsletters: (newsletters: Newsletters | null | undefined) =>
        setNewslettersContext(
          (previousNewslettersContext: NewslettersContextValue) => ({
            ...previousNewslettersContext,
            error: null,
            isLoading: false,
            newsletters,
          }),
        ),
    })

  asyncNewsletters = useAsync(async () => {
    if (includeDisabled && (isUserLoading || isEmpty(user))) {
      return null
    }

    let newsletters: Newsletters = []
    try {
      newsletters = (await endpoints.newsletters.list(includeDisabled)) ?? []
    } catch (error) {
      const actualError = error as Error
      logger.error(
        `[EDITOR][NEWSLETTERS] Unable to load the list of newsletters: "${actualError.message}"`,
        {},
        actualError,
      )

      toast.error($t('toast.error.editor.newsletter.unexpected'))

      newslettersContext.setError?.(error as Error)

      throw error
    }

    newslettersContext.setNewsletters?.(sortBy(['name'], newsletters))

    return newsletters
  }, [user])

  return (
    <NewslettersContext.Provider value={newslettersContext}>
      {children}
    </NewslettersContext.Provider>
  )
}
